import { useEffect, useMemo, useState } from "react";
import {
  fetchSiteDesignerCurrent, fetchSiteDesignerRevisions, previewSiteDesigner,
  rollbackSiteDesigner, saveSiteDesigner,
} from "../api/client";

function humanize(value) {
  return String(value || "").replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function schemaSections(schema) {
  return Object.keys(schema || {}).filter((key) => !["schema", "top_level", "definitions"].includes(key));
}

const typeFromRule = (rule, existing) => {
  if (rule === "optional_bool") return "boolean";
  if (rule === "optional_string_list") return "array";
  if (rule.startsWith("optional_int") || rule === "optional_positive_int") return "number";
  if (rule === "optional_enum") return "enum";
  if (Array.isArray(existing)) return "array";
  if (typeof existing === "boolean") return "boolean";
  if (typeof existing === "number") return "number";
  return "string";
};

export function schemaFields(schema, config) {
  const fields = [];
  const walk = (node, path = []) => {
    if (!node || typeof node !== "object") return;
    for (const [rule, definition] of Object.entries(node)) {
      if (rule === "optional_blocks" && definition && typeof definition === "object") {
        for (const [name, child] of Object.entries(definition)) walk(child, [...path, name]);
      } else if (rule === "optional_enum" && definition && typeof definition === "object") {
        for (const [name, values] of Object.entries(definition)) fields.push({
          path: [...path, name], type: "enum", values: Array.isArray(values) ? values : [],
        });
      } else if (rule === "optional_int_range" && definition && typeof definition === "object") {
        for (const [name, range] of Object.entries(definition)) fields.push({
          path: [...path, name], type: "number", min: range?.min, max: range?.max,
        });
      } else if (["required", "required_absolute", "optional_absolute", "optional_positive_int",
        "optional_bool", "optional_int_gt_zero", "optional_int_ge_negative_one", "optional_int_gt_one",
        "optional_string", "optional_string_list", "optional_disk_list"].includes(rule)
        && Array.isArray(definition)) {
        for (const name of definition) {
          const fieldPath = [...path, name];
          const value = fieldPath.reduce((at, key) => at?.[key], config);
          fields.push({ path: fieldPath, type: typeFromRule(rule, value) });
        }
      }
    }
  };
  const configSchema = { ...(schema || {}) };
  delete configSchema.schema;
  delete configSchema.top_level;
  for (const [section, sectionSchema] of Object.entries(configSchema)) walk(sectionSchema, [section]);
  const unique = new Map(fields.map((field) => [field.path.join("."), field]));
  return [...unique.values()].sort((a, b) => a.path.join(".").localeCompare(b.path.join(".")));
}

function setPath(value, path, next) {
  const copy = structuredClone(value);
  let cursor = copy;
  for (const part of path.slice(0, -1)) {
    if (!cursor[part] || typeof cursor[part] !== "object" || Array.isArray(cursor[part])) cursor[part] = {};
    cursor = cursor[part];
  }
  cursor[path.at(-1)] = next;
  return copy;
}

function secretPath(path) {
  return /password|secret|token|credential|private[_-]?key|api[_-]?key/i.test(path.join("."));
}

export default function SiteDesigner({ user }) {
  const [source, setSource] = useState(null);
  const [text, setText] = useState("");
  const [revisions, setRevisions] = useState([]);
  const [selectedRevision, setSelectedRevision] = useState("");
  const [preview, setPreview] = useState(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [arrayDrafts, setArrayDrafts] = useState({});
  const [busy, setBusy] = useState(false);
  const sections = useMemo(() => schemaSections(source?.schema), [source]);
  const fields = useMemo(() => schemaFields(source?.schema, source?.config || {}), [source]);

  const load = async () => {
    setBusy(true);
    setError("");
    try {
      const current = await fetchSiteDesignerCurrent();
      const saved = await fetchSiteDesignerRevisions();
      setSource(current);
      setText(JSON.stringify(current.config, null, 2));
      setFieldErrors({});
      setArrayDrafts({});
      setRevisions(saved);
      setPreview(null);
      setMessage("");
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load(); }, []);

  const parse = () => {
    try {
      const value = JSON.parse(text);
      if (!value || typeof value !== "object" || Array.isArray(value)) throw new Error("Draft root must be an object.");
      return value;
    } catch (err) {
      throw new Error(err.message || "Draft must be valid JSON.");
    }
  };

  const validate = async () => {
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const result = await previewSiteDesigner(parse(), source.revision);
      setPreview(result);
      setMessage("Draft passed canonical server validation. Review the redacted preview before saving.");
    } catch (err) {
      setPreview(null);
      setError(err.message);
      const matched = fields.find(({ path }) => err.message.toLowerCase().includes(path.join(".").toLowerCase()));
      setFieldErrors(matched ? { [matched.path.join(".")]: err.message } : {});
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    if (!preview) return;
    setBusy(true);
    setError("");
    try {
      const result = await saveSiteDesigner(parse(), source.revision);
      setMessage(`Saved validated revision ${result.revision}. Runtime configuration was not applied.`);
      setPreview(null);
      setRevisions(await fetchSiteDesignerRevisions());
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const rollback = async () => {
    if (!selectedRevision || !window.confirm(`Save ${selectedRevision} as a new rollback revision? This will not apply it.`)) return;
    setBusy(true);
    setError("");
    try {
      const result = await rollbackSiteDesigner(selectedRevision, source.revision);
      setMessage(`Rollback draft saved as ${result.revision}. Runtime configuration was not applied.`);
      setRevisions(await fetchSiteDesignerRevisions());
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (!source && !error) return <main className="login-screen" role="status">Loading Site Designer…</main>;

  return (
    <main className="site-designer" aria-labelledby="site-designer-title">
      <header className="site-designer-header">
        <div><p className="studio-section-kicker">OPERATOR / CONFIGURATION</p>
          <h1 id="site-designer-title">Site Designer</h1>
          <p>Draft and validate an Ignite configuration revision. Saving does not apply it to the running system.</p>
        </div>
        <a href="/operator">Back to operator console</a>
      </header>
      <div className="site-designer-state" aria-live="polite">
        <span><b>Draft</b> {preview ? "Validated" : "Editing"}</span>
        <span><b>Saved revision</b> {source?.saved_revision || "None"}</span>
        <span><b>Applied runtime</b> Unknown; source configuration is read only</span>
      </div>
      {error && <div role="alert" className="site-designer-error">{error}</div>}
      {message && <div role="status" className="site-designer-message">{message}</div>}
      {source && <>
        <section className="site-designer-meta" aria-label="Configuration sections">
          <h2>Schema sections</h2>
          <ul>{sections.map((section) => <li key={section} title={`Configuration section: ${section}`}>{humanize(section)}</li>)}</ul>
          <p>Secret values are redacted. Unchanged redacted fields are preserved by the server.</p>
          <p>Source revision: <code>{source.revision}</code></p>
        </section>
        <section className="site-designer-fields" aria-label="Schema-backed configuration fields">
          <h2>Configuration fields</h2>
          {fields.map((field) => {
            const path = field.path.join(".");
            const value = field.path.reduce((at, key) => at?.[key], source.config);
            const label = humanize(field.path.at(-1));
            const update = (next) => {
              const config = setPath(source.config, field.path, next);
              setSource({ ...source, config });
              setText(JSON.stringify(config, null, 2));
              setPreview(null);
              setFieldErrors({});
            };
            return <label className="site-designer-field" key={path} title={`${path}${field.values?.length ? ` · Valid values: ${field.values.join(", ")}` : ""}`}>
              <span><b>{label}</b><code>{path}</code></span>
              {secretPath(field.path) ? <span className="site-designer-secret">{value ? "Configured (value hidden)" : "Not configured"}</span>
                : field.type === "boolean" ? <input aria-label={path} type="checkbox" checked={value === true} onChange={(event) => update(event.target.checked)} />
                  : field.type === "enum" && field.values.length ? <select aria-label={path} value={value ?? ""} onChange={(event) => update(event.target.value)}>
                    <option value="">Select a value</option>{field.values.map((option) => <option key={option} value={option}>{option}</option>)}
                  </select>
                    : field.type === "array" ? <textarea aria-label={path} rows="2"
                      value={arrayDrafts[path] ?? (Array.isArray(value) ? JSON.stringify(value, null, 2) : "[]")}
                      onChange={(event) => {
                        setArrayDrafts((previous) => ({ ...previous, [path]: event.target.value }));
                        try {
                          const parsed = JSON.parse(event.target.value);
                          if (Array.isArray(parsed)) update(parsed);
                        } catch { /* Keep incomplete JSON in the field buffer until it is valid. */ }
                      }} />
                      : <input aria-label={path} type={field.type === "number" ? "number" : "text"}
                        min={field.min} max={field.max} value={value ?? ""}
                        aria-invalid={Boolean(fieldErrors[path])} aria-describedby={fieldErrors[path] ? `${path}-error` : undefined}
                        onChange={(event) => update(field.type === "number" && event.target.value !== "" ? Number(event.target.value) : event.target.value)} />}
              {field.values?.length > 0 && <small>Valid values: {field.values.join(", ")}</small>}
              {fieldErrors[path] && <small id={`${path}-error`} role="alert">{fieldErrors[path]}</small>}
            </label>;
          })}
        </section>
        <label htmlFor="site-designer-json"><b>Configuration draft (JSON)</b></label>
        <textarea id="site-designer-json" value={text} spellCheck="false"
          onChange={(event) => { setText(event.target.value); setPreview(null); try { const config = JSON.parse(event.target.value); if (config && typeof config === "object" && !Array.isArray(config)) { setArrayDrafts({}); setSource({ ...source, config }); } } catch { /* Keep invalid draft text for visible validation feedback. */ } }}
          aria-describedby="site-designer-help" />
        <p id="site-designer-help">The server converts the draft to canonical YAML and validates it against the active Ignite schema. Unknown keys are retained.</p>
        <div className="site-designer-actions">
          <button type="button" onClick={load} disabled={busy}>Reload canonical configuration</button>
          <button type="button" onClick={validate} disabled={busy}>Validate and preview</button>
          <button type="button" onClick={save} disabled={busy || !preview}>Save validated revision</button>
          <button type="button" disabled title={source.apply?.reason || "Apply is unavailable."}>Apply unavailable</button>
        </div>
        {preview && <section aria-labelledby="preview-title" className="site-designer-preview">
          <h2 id="preview-title">Redacted validated preview</h2>
          <p>Candidate SHA-256: <code>{preview.candidate_revision}</code></p>
          <h3>Before and after</h3>
          {preview.changes?.length ? <ul>{preview.changes.map((change) => <li key={change.path}>
            <code>{change.path}</code>: <code>{JSON.stringify(change.before)}</code> → <code>{JSON.stringify(change.after)}</code>
          </li>)}</ul> : <p>No configuration changes.</p>}
          <pre>{JSON.stringify(preview.preview, null, 2)}</pre>
        </section>}
        <section className="site-designer-revisions" aria-labelledby="revisions-title">
          <h2 id="revisions-title">Saved validated revisions</h2>
          <label htmlFor="site-designer-revision">Revision</label>
          <select id="site-designer-revision" value={selectedRevision} onChange={(event) => setSelectedRevision(event.target.value)}>
            <option value="">Choose a revision</option>
            {revisions.map((revision) => <option key={revision.revision} value={revision.revision}>{revision.saved_at} · {revision.revision}</option>)}
          </select>
          <button type="button" onClick={rollback} disabled={busy || !selectedRevision}>Save rollback draft</button>
        </section>
      </>}
    </main>
  );
}
